/**
 * WhatsApp Template Validator
 * 
 * Validates WhatsApp message templates against Meta's official policies
 * and provides approval probability estimation with actionable improvement tips.
 * 
 * Based on:
 * - https://developers.facebook.com/docs/whatsapp/message-templates/guidelines/
 * - https://developers.facebook.com/docs/whatsapp/updates-to-pricing/new-template-guidelines
 * - https://business.whatsapp.com/policy
 */

// ========================== CONSTANTS ==========================

const SEVERITY = {
  CRITICAL: "critical",   // Will almost certainly cause rejection
  HIGH: "high",           // Very likely to cause rejection
  MEDIUM: "medium",       // May cause rejection or category change
  LOW: "low",             // Best practice recommendation
  INFO: "info",           // Informational tip
};

const SEVERITY_LABELS = {
  critical: "Crítico",
  high: "Alto",
  medium: "Médio",
  low: "Baixo",
  info: "Dica",
};

const SEVERITY_COLORS = {
  critical: "#d32f2f",
  high: "#f44336",
  medium: "#ff9800",
  low: "#2196f3",
  info: "#9e9e9e",
};

const SEVERITY_SCORE_PENALTY = {
  critical: 40,
  high: 25,
  medium: 12,
  low: 5,
  info: 0,
};

// Maximum character limits per Meta API
const LIMITS = {
  NAME_MAX: 512,
  HEADER_TEXT_MAX: 60,
  BODY_MAX: 1024,
  FOOTER_MAX: 60,
  BUTTON_TEXT_MAX: 25,
  BUTTON_URL_MAX: 2000,
  BUTTON_PHONE_MAX: 20,
  MAX_BUTTONS: 10,
  MAX_QUICK_REPLY_BUTTONS: 10,
  MAX_CTA_BUTTONS: 2,
  AUTH_PARAM_MAX: 15,
};

// Prohibited content patterns
const PROHIBITED_PATTERNS = {
  CREDIT_CARD: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
  CPF: /\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g,
  FULL_PHONE: /\b\+?\d{2,3}\s?\(?\d{2}\)?\s?\d{4,5}[-\s]?\d{4}\b/g,
  SENSITIVE_DATA: /\b(senha|password|pin\s*code|cvv|cvc)\b/gi,
};

// Words/phrases that suggest promotional content (marketing indicators)
const MARKETING_INDICATORS = [
  /\b(promoção|promo|oferta|desconto|cupom|coupon|sale|grátis|free|brinde)\b/gi,
  /\b(compre|comprar|adquira|aproveite|não\s+perca|imperdível)\b/gi,
  /\b(exclusiv[oa]|limitad[oa]|somente\s+hoje|última\s+chance)\b/gi,
  /\b(clique\s+aqui|saiba\s+mais|confira|visite\s+nosso|conheça)\b/gi,
  /\b(R\$\s*\d|%\s*off|%\s*de\s*desconto)\b/gi,
  /\b(upgrade|premium|assine|inscreva)\b/gi,
];

// Words that suggest utility content
const UTILITY_INDICATORS = [
  /\b(pedido|encomenda|order|tracking|rastreio|rastreamento)\b/gi,
  /\b(confirmação|confirmar|confirmado|agendamento|agendado)\b/gi,
  /\b(entrega|delivery|shipped|enviado|despachado)\b/gi,
  /\b(fatura|boleto|pagamento|payment|cobrança)\b/gi,
  /\b(atualização|update|status|notificação)\b/gi,
  /\b(suporte|support|atendimento|chamado|ticket)\b/gi,
  /\b(lembrete|reminder|aviso|alerta)\b/gi,
];

// Spam trigger words
const SPAM_TRIGGERS = [
  /\b(urgente|urgent|atenção|attention)\b/gi,
  /\b(ganhe\s+dinheiro|renda\s+extra|investimento\s+garantido)\b/gi,
  /\b(100%\s+garantido|sem\s+risco|lucro\s+certo)\b/gi,
  /\b(clique\s+agora|aja\s+agora|responda\s+já)\b/gi,
  /!!!|(\?{3,})|(!{3,})/g,
  /[A-ZÁÉÍÓÚÀÂÊÔÃÕÇ\s]{20,}/g, // Long ALL CAPS sequences
];

// ========================== VALIDATION RULES ==========================

/**
 * Extract variables from template text
 * Supports both positional {{1}} and named {{var_name}} formats
 */
function extractVariables(text) {
  if (!text) return [];
  const matches = text.match(/\{\{([^}]+)\}\}/g) || [];
  return matches.map(m => m.replace(/[{}]/g, "").trim());
}

/**
 * Check if variable is positional (numeric)
 */
function isPositionalVar(v) {
  return /^\d+$/.test(v);
}

/**
 * Check if variable is named
 */
function isNamedVar(v) {
  return /^[a-z][a-z0-9_]*$/.test(v);
}

/**
 * Count words in text (excluding variables)
 */
function countWords(text) {
  if (!text) return 0;
  return text.replace(/\{\{[^}]+\}\}/g, "").trim().split(/\s+/).filter(Boolean).length;
}

// ========================== RULE FUNCTIONS ==========================

function validateTemplateName(name) {
  const issues = [];
  if (!name) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "name_required",
      message: "O nome do template é obrigatório.",
      tip: "Defina um nome descritivo para o template.",
    });
    return issues;
  }

  if (!/^[a-z0-9_]+$/.test(name)) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "name_format",
      message: "O nome do template aceita apenas letras minúsculas, números e underscores.",
      tip: 'Use formato como "confirmacao_pedido" ou "aviso_entrega_v2".',
    });
  }

  if (name.length > LIMITS.NAME_MAX) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "name_length",
      message: `Nome excede ${LIMITS.NAME_MAX} caracteres (atual: ${name.length}).`,
      tip: "Reduza o nome do template.",
    });
  }

  if (name.length < 3) {
    issues.push({
      severity: SEVERITY.MEDIUM,
      rule: "name_too_short",
      message: "Nome muito curto. Templates com nomes genéricos podem ser rejeitados.",
      tip: "Use um nome descritivo como 'confirmacao_pedido_v1'.",
    });
  }

  return issues;
}

function validateHeaderText(headerText, category) {
  const issues = [];
  if (!headerText) return issues;

  if (headerText.length > LIMITS.HEADER_TEXT_MAX) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "header_length",
      message: `Cabeçalho excede ${LIMITS.HEADER_TEXT_MAX} caracteres (atual: ${headerText.length}).`,
      tip: `Reduza o cabeçalho para no máximo ${LIMITS.HEADER_TEXT_MAX} caracteres.`,
    });
  }

  const headerVars = extractVariables(headerText);
  if (headerVars.length > 1) {
    issues.push({
      severity: SEVERITY.HIGH,
      rule: "header_max_vars",
      message: "O cabeçalho de texto aceita no máximo 1 variável.",
      tip: "Mova variáveis extras para o corpo da mensagem.",
    });
  }

  if (category === "AUTHENTICATION") {
    if (headerText.trim().length > 0) {
      issues.push({
        severity: SEVERITY.MEDIUM,
        rule: "auth_no_header",
        message: "Templates de autenticação geralmente não usam cabeçalho personalizado.",
        tip: "Templates de autenticação devem ser simples: código + instruções.",
      });
    }
  }

  return issues;
}

function validateBodyText(bodyText, category, sampleValues) {
  const issues = [];

  if (!bodyText || !bodyText.trim()) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "body_required",
      message: "O corpo da mensagem é obrigatório.",
      tip: "Escreva o conteúdo principal da mensagem.",
    });
    return issues;
  }

  // Length check
  if (bodyText.length > LIMITS.BODY_MAX) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "body_length",
      message: `Corpo excede ${LIMITS.BODY_MAX} caracteres (atual: ${bodyText.length}).`,
      tip: "Reduza o texto do corpo da mensagem.",
    });
  }

  // Variable validation
  const vars = extractVariables(bodyText);
  if (vars.length > 0) {
    // Check mixing of positional and named vars
    const hasPositional = vars.some(isPositionalVar);
    const hasNamed = vars.some(isNamedVar);
    const hasInvalid = vars.some(v => !isPositionalVar(v) && !isNamedVar(v));

    if (hasPositional && hasNamed) {
      issues.push({
        severity: SEVERITY.CRITICAL,
        rule: "vars_mixed_format",
        message: "Não misture variáveis posicionais ({{1}}) com nomeadas ({{nome}}).",
        tip: "Use apenas um formato: posicional {{1}}, {{2}} OU nomeado {{nome}}, {{pedido}}.",
      });
    }

    if (hasInvalid) {
      issues.push({
        severity: SEVERITY.HIGH,
        rule: "vars_invalid_format",
        message: "Variáveis devem ser numéricas ({{1}}) ou nomeadas em minúsculas com underscore ({{nome_cliente}}).",
        tip: "Corrija o formato das variáveis. Exemplos válidos: {{1}}, {{nome}}, {{numero_pedido}}.",
      });
    }

    // Check positional sequence
    if (hasPositional && !hasNamed) {
      const nums = vars.filter(isPositionalVar).map(Number).sort((a, b) => a - b);
      for (let i = 0; i < nums.length; i++) {
        if (nums[i] !== i + 1) {
          issues.push({
            severity: SEVERITY.HIGH,
            rule: "vars_sequence",
            message: `Variáveis posicionais devem ser sequenciais começando em 1. Encontrado: ${nums.join(", ")}.`,
            tip: "Renumere as variáveis: {{1}}, {{2}}, {{3}}, etc.",
          });
          break;
        }
      }
    }

    // CRITICAL: Sample values for variables
    const missingExamples = [];
    vars.forEach(v => {
      if (!sampleValues || !sampleValues[v] || !sampleValues[v].trim()) {
        missingExamples.push(`{{${v}}}`);
      }
    });

    if (missingExamples.length > 0) {
      issues.push({
        severity: SEVERITY.CRITICAL,
        rule: "vars_no_sample",
        message: `Variáveis sem texto de amostra: ${missingExamples.join(", ")}.`,
        tip: "IMPORTANTE: Preencha os textos de amostra para TODAS as variáveis. Templates sem amostras são frequentemente rejeitados pela Meta.",
      });
    }

    // Content is only variables
    const textOnly = bodyText.replace(/\{\{[^}]+\}\}/g, "").trim();
    if (textOnly.length < 10) {
      issues.push({
        severity: SEVERITY.HIGH,
        rule: "body_only_vars",
        message: "O corpo contém pouco texto fixo além das variáveis.",
        tip: 'A Meta rejeita templates onde o conteúdo é só variáveis (ex: "{{1}}"). Adicione texto explicativo claro em torno das variáveis.',
      });
    }
  }

  // Word count check
  const wordCount = countWords(bodyText);
  if (wordCount < 4) {
    issues.push({
      severity: SEVERITY.HIGH,
      rule: "body_too_short",
      message: "Corpo da mensagem muito curto.",
      tip: "Templates com conteúdo vago como 'Parabéns!' ou 'Olá!' são rejeitados. Forneça contexto claro.",
    });
  }

  // Check for prohibited data
  if (PROHIBITED_PATTERNS.CREDIT_CARD.test(bodyText)) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "prohibited_credit_card",
      message: "Não inclua números completos de cartão de crédito.",
      tip: "Use apenas os últimos 4 dígitos: **** **** **** 1234",
    });
  }

  if (PROHIBITED_PATTERNS.CPF.test(bodyText)) {
    issues.push({
      severity: SEVERITY.HIGH,
      rule: "prohibited_cpf",
      message: "Não inclua números completos de CPF.",
      tip: "Use formato parcial: ***.123.456-**",
    });
  }

  if (PROHIBITED_PATTERNS.SENSITIVE_DATA.test(bodyText)) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "prohibited_sensitive",
      message: "Não solicite ou envie dados sensíveis (senhas, PINs, CVV).",
      tip: "Nunca inclua ou solicite informações de segurança em templates.",
    });
  }

  // Authentication specific validations
  if (category === "AUTHENTICATION") {
    // Check for URLs
    if (/https?:\/\/\S+/i.test(bodyText)) {
      issues.push({
        severity: SEVERITY.CRITICAL,
        rule: "auth_no_urls",
        message: "Templates de autenticação não podem conter URLs.",
        tip: "Remova qualquer link. Use apenas o código OTP e instruções textuais.",
      });
    }

    // Check for emojis
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2700}-\u{27BF}\u{2600}-\u{26FF}]/u;
    if (emojiRegex.test(bodyText)) {
      issues.push({
        severity: SEVERITY.HIGH,
        rule: "auth_no_emojis",
        message: "Templates de autenticação não podem conter emojis.",
        tip: "Remova todos os emojis do texto.",
      });
    }

    // Check param length for auth (max 15 chars)
    if (vars.length > 0 && sampleValues) {
      vars.forEach(v => {
        const sample = sampleValues[v];
        if (sample && sample.length > LIMITS.AUTH_PARAM_MAX) {
          issues.push({
            severity: SEVERITY.HIGH,
            rule: "auth_param_length",
            message: `Parâmetro ${v} da autenticação deve ter no máximo ${LIMITS.AUTH_PARAM_MAX} caracteres.`,
            tip: "Códigos OTP devem ser curtos (ex: 6 dígitos).",
          });
        }
      });
    }
  }

  return issues;
}

function validateFooterText(footerText) {
  const issues = [];
  if (!footerText) return issues;

  if (footerText.length > LIMITS.FOOTER_MAX) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "footer_length",
      message: `Rodapé excede ${LIMITS.FOOTER_MAX} caracteres (atual: ${footerText.length}).`,
      tip: `Reduza para no máximo ${LIMITS.FOOTER_MAX} caracteres.`,
    });
  }

  return issues;
}

function validateCategoryAlignment(bodyText, headerText, category) {
  const issues = [];
  const fullText = `${headerText || ""} ${bodyText || ""}`;

  if (category === "UTILITY") {
    // Check if it looks like marketing content
    let marketingCount = 0;
    MARKETING_INDICATORS.forEach(pattern => {
      if (pattern.test(fullText)) marketingCount++;
      pattern.lastIndex = 0; // Reset regex
    });

    if (marketingCount >= 2) {
      issues.push({
        severity: SEVERITY.HIGH,
        rule: "category_mismatch_marketing",
        message: `Template categorizado como Utilitário contém ${marketingCount} indicadores de conteúdo promocional.`,
        tip: "Se o template contém ofertas, descontos, promoções ou call-to-action de vendas, ele deve ser categorizado como MARKETING. A Meta reclassifica automaticamente e pode restringir sua conta.",
      });
    } else if (marketingCount === 1) {
      issues.push({
        severity: SEVERITY.MEDIUM,
        rule: "category_possible_marketing",
        message: "Template utilitário pode conter conteúdo promocional.",
        tip: "Textos mistos (utilitário + marketing) são reclassificados como marketing. Mantenha o template puramente transacional.",
      });
    }

    // Check if utility template is specific enough
    let utilityCount = 0;
    UTILITY_INDICATORS.forEach(pattern => {
      if (pattern.test(fullText)) utilityCount++;
      pattern.lastIndex = 0;
    });

    if (utilityCount === 0 && marketingCount === 0) {
      issues.push({
        severity: SEVERITY.MEDIUM,
        rule: "utility_not_specific",
        message: "Template utilitário sem indicadores de contexto transacional.",
        tip: "Templates utilitários devem estar ligados a uma ação do usuário (pedido, agendamento, suporte). Inclua referências específicas como número do pedido, data, etc.",
      });
    }
  }

  if (category === "MARKETING") {
    // Marketing should have clear opt-out possibility
    const hasOptOut = /\b(responda\s+sair|envie\s+pare|opt[\s-]*out|cancelar\s+inscri|descadastr)/gi.test(fullText);
    if (!hasOptOut) {
      issues.push({
        severity: SEVERITY.LOW,
        rule: "marketing_no_optout",
        message: "Template de marketing sem instrução de opt-out.",
        tip: "Boa prática: inclua instrução para o cliente sair da lista (ex: 'Responda SAIR para não receber mais mensagens').",
      });
    }
  }

  if (category === "AUTHENTICATION") {
    // Check if it actually contains auth-like content
    const hasOTP = /\b(código|code|verificação|verification|otp|token|confirma)\b/gi.test(fullText);
    if (!hasOTP) {
      issues.push({
        severity: SEVERITY.HIGH,
        rule: "auth_no_code",
        message: "Template de autenticação deve conter um código de verificação.",
        tip: "Use templates da Biblioteca de Templates do Meta para autenticação. O template deve conter algo como '{{1}} é seu código de verificação'.",
      });
    }
  }

  return issues;
}

function validateSpamIndicators(bodyText, headerText) {
  const issues = [];
  const fullText = `${headerText || ""} ${bodyText || ""}`;

  let spamCount = 0;
  const spamDetails = [];

  SPAM_TRIGGERS.forEach(pattern => {
    const matches = fullText.match(pattern);
    if (matches) {
      spamCount += matches.length;
      spamDetails.push(matches[0]);
    }
    pattern.lastIndex = 0;
  });

  if (spamCount >= 3) {
    issues.push({
      severity: SEVERITY.HIGH,
      rule: "spam_high",
      message: `Texto contém ${spamCount} indicadores de spam.`,
      tip: "Evite uso excessivo de CAPS LOCK, pontuação excessiva (!!!), e palavras como 'urgente', 'aja agora'. Isso reduz a qualidade e pode causar rejeição.",
    });
  } else if (spamCount >= 1) {
    issues.push({
      severity: SEVERITY.MEDIUM,
      rule: "spam_moderate",
      message: "Texto contém linguagem que pode ser interpretada como spam.",
      tip: `Considere reformular: "${spamDetails[0]}". Tom profissional e objetivo aumenta chance de aprovação.`,
    });
  }

  return issues;
}

function validateBestPractices(bodyText, headerText, footerText, category, sampleValues) {
  const issues = [];
  const vars = extractVariables(bodyText || "");

  // Recommend named parameters over positional
  if (vars.length > 0 && vars.every(isPositionalVar)) {
    issues.push({
      severity: SEVERITY.INFO,
      rule: "prefer_named_params",
      message: "Considere usar parâmetros nomeados em vez de posicionais.",
      tip: "Parâmetros nomeados ({{nome}}, {{pedido}}) são mais legíveis e menos propensos a erros que posicionais ({{1}}, {{2}}).",
    });
  }

  // Recommend greeting for marketing
  if (category === "MARKETING" && bodyText && !/\b(olá|oi|bom\s+dia|boa\s+tarde|boa\s+noite|prezad|car[oa]|hi|hello)\b/gi.test(bodyText)) {
    issues.push({
      severity: SEVERITY.LOW,
      rule: "marketing_greeting",
      message: "Template de marketing sem saudação.",
      tip: "Comece com uma saudação personalizada (ex: 'Olá {{nome}}!'). Isso aumenta engajamento e reduz bloqueios.",
    });
  }

  // Check if there's a clear CTA
  if (category === "MARKETING" && bodyText) {
    const hasCTA = /\b(clique|acesse|visite|responda|saiba\s+mais|confira|veja|compre|aproveite|agende)\b/gi.test(bodyText);
    if (!hasCTA) {
      issues.push({
        severity: SEVERITY.INFO,
        rule: "marketing_no_cta",
        message: "Template de marketing sem chamada para ação clara.",
        tip: "Inclua uma ação clara para o destinatário (ex: 'Clique no botão abaixo para conferir').",
      });
    }
  }

  // Recommend footer
  if (!footerText && category !== "AUTHENTICATION") {
    issues.push({
      severity: SEVERITY.INFO,
      rule: "no_footer",
      message: "Considere adicionar um rodapé.",
      tip: "O rodapé pode conter o nome da empresa ou 'Não responda a esta mensagem'. Transmite profissionalismo.",
    });
  }

  // Check variable sample quality
  if (vars.length > 0 && sampleValues) {
    vars.forEach(v => {
      const sample = sampleValues[v];
      if (sample) {
        // Check if sample is too generic
        if (/^(test|teste|xxx|aaa|123|abc)$/i.test(sample.trim())) {
          issues.push({
            severity: SEVERITY.MEDIUM,
            rule: "sample_too_generic",
            message: `Texto de amostra genérico para {{${v}}}: "${sample}".`,
            tip: "Use amostras realistas. Em vez de 'teste', use um nome real como 'Maria' ou um número de pedido real como '#12345'.",
          });
        }

        // Check if sample is too long for body display
        if (sample.length > 50) {
          issues.push({
            severity: SEVERITY.LOW,
            rule: "sample_too_long",
            message: `Amostra para {{${v}}} é muito longa (${sample.length} caracteres).`,
            tip: "Mantenha amostras concisas e representativas.",
          });
        }
      }
    });
  }

  return issues;
}

// ========================== MAIN VALIDATOR ==========================

/**
 * Validate buttons array
 */
function validateButtons(buttons, category) {
  const issues = [];
  if (!buttons || buttons.length === 0) return issues;

  // Count by type
  const quickReplies = buttons.filter(b => b.type === "QUICK_REPLY");
  const ctaButtons = buttons.filter(b => b.type === "URL" || b.type === "PHONE_NUMBER");

  // Cannot mix quick_reply with CTA buttons
  if (quickReplies.length > 0 && ctaButtons.length > 0) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "buttons_mixed_types",
      message: "Não é possível misturar botões de Resposta Rápida com botões de URL/Telefone.",
      tip: "Use apenas um tipo: Respostas Rápidas OU botões de ação (URL/Telefone).",
    });
  }

  // Max buttons check
  if (buttons.length > LIMITS.MAX_BUTTONS) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "buttons_max_exceeded",
      message: `Máximo de ${LIMITS.MAX_BUTTONS} botões permitidos (atual: ${buttons.length}).`,
      tip: "Remova botões excedentes.",
    });
  }

  // Max CTA buttons
  if (ctaButtons.length > LIMITS.MAX_CTA_BUTTONS) {
    issues.push({
      severity: SEVERITY.CRITICAL,
      rule: "buttons_max_cta",
      message: `Máximo de ${LIMITS.MAX_CTA_BUTTONS} botões de ação (URL/Telefone) permitidos (atual: ${ctaButtons.length}).`,
      tip: "Use no máximo 1 botão de URL e 1 de Telefone.",
    });
  }

  // Validate each button  
  buttons.forEach((btn, idx) => {
    const num = idx + 1;

    if (!btn.text || !btn.text.trim()) {
      issues.push({
        severity: SEVERITY.CRITICAL,
        rule: `button_${num}_no_text`,
        message: `Botão ${num}: texto é obrigatório.`,
        tip: "Preencha o texto do botão.",
      });
    } else if (btn.text.length > LIMITS.BUTTON_TEXT_MAX) {
      issues.push({
        severity: SEVERITY.CRITICAL,
        rule: `button_${num}_text_long`,
        message: `Botão ${num}: texto excede ${LIMITS.BUTTON_TEXT_MAX} caracteres (atual: ${btn.text.length}).`,
        tip: "Reduza o texto do botão.",
      });
    }

    if (btn.type === "URL") {
      if (!btn.url || !btn.url.trim()) {
        issues.push({
          severity: SEVERITY.CRITICAL,
          rule: `button_${num}_no_url`,
          message: `Botão ${num} (URL): endereço é obrigatório.`,
          tip: "Preencha a URL do botão (ex: https://seusite.com.br).",
        });
      } else {
        // Validate URL format (replace variables with placeholder for validation)
        const urlForValidation = btn.url.replace(/\{\{[^}]+\}\}/g, "exemplo");
        if (!/^https?:\/\/.+/i.test(urlForValidation)) {
          issues.push({
            severity: SEVERITY.HIGH,
            rule: `button_${num}_invalid_url`,
            message: `Botão ${num}: URL deve começar com http:// ou https://`,
            tip: "Exemplo: https://www.suaempresa.com.br/pagina/{{1}}",
          });
        }
      }

      // URL with variable
      const urlVars = extractVariables(btn.url);
      if (urlVars.length > 1) {
        issues.push({
          severity: SEVERITY.HIGH,
          rule: `button_${num}_url_multi_var`,
          message: `Botão ${num}: URL aceita no máximo 1 variável dinâmica.`,
          tip: "Use a variável somente no final da URL: https://site.com/pedido/{{1}}",
        });
      }
      if (urlVars.length > 0 && (!btn.urlExample || !btn.urlExample.trim())) {
        issues.push({
          severity: SEVERITY.CRITICAL,
          rule: `button_${num}_url_no_example`,
          message: `Botão ${num}: URL com variável dinâmica requer um valor de exemplo.`,
          tip: "Forneça um exemplo de valor para a variável da URL (ex: '12345' para um ID de pedido). A Meta exige isso para aprovação.",
        });
      }
    }

    if (btn.type === "PHONE_NUMBER") {
      if (!btn.phoneNumber || !btn.phoneNumber.trim()) {
        issues.push({
          severity: SEVERITY.CRITICAL,
          rule: `button_${num}_no_phone`,
          message: `Botão ${num} (Telefone): número é obrigatório.`,
          tip: "Preencha o número com código do país (ex: +5511999999999).",
        });
      } else if (!/^\+\d{6,20}$/.test(btn.phoneNumber.replace(/[\s-]/g, ""))) {
        issues.push({
          severity: SEVERITY.HIGH,
          rule: `button_${num}_invalid_phone`,
          message: `Botão ${num}: formato de telefone inválido.`,
          tip: "Use formato internacional com +: +5511999999999",
        });
      }
    }

    if (btn.type === "QUICK_REPLY") {
      // Check for duplicate text
      const duplicates = buttons.filter(
        (b, i) => i !== idx && b.type === "QUICK_REPLY" && b.text?.toLowerCase() === btn.text?.toLowerCase()
      );
      if (duplicates.length > 0) {
        issues.push({
          severity: SEVERITY.HIGH,
          rule: `button_${num}_duplicate`,
          message: `Botão ${num}: texto duplicado com outro botão de resposta rápida.`,
          tip: "Cada botão de resposta rápida deve ter um texto único.",
        });
      }
    }
  });

  // Auth templates: buttons must be copy code or one-tap
  if (category === "AUTHENTICATION") {
    const nonAuthButtons = buttons.filter(b => b.type !== "COPY_CODE" && b.type !== "ONE_TAP");
    if (nonAuthButtons.length > 0) {
      issues.push({
        severity: SEVERITY.HIGH,
        rule: "auth_invalid_buttons",
        message: "Templates de autenticação só permitem botões de Copiar Código.",
        tip: "Use apenas o botão 'Copiar Código' para templates de autenticação.",
      });
    }
  }

  return issues;
}

/**
 * Validate header media format
 */
function validateHeaderMedia(headerFormat, headerMediaUrl, category) {
  const issues = [];

  if (headerFormat === "IMAGE" || headerFormat === "VIDEO" || headerFormat === "DOCUMENT") {
    if (category === "AUTHENTICATION") {
      issues.push({
        severity: SEVERITY.CRITICAL,
        rule: "auth_no_media",
        message: "Templates de autenticação não podem conter mídia no cabeçalho.",
        tip: "Remova a mídia ou altere a categoria.",
      });
    }

    if (!headerMediaUrl || !headerMediaUrl.trim()) {
      issues.push({
        severity: SEVERITY.CRITICAL,
        rule: "header_media_no_sample",
        message: `Cabeçalho ${headerFormat}: forneça uma URL de amostra da mídia.`,
        tip: "A Meta exige que templates com cabeçalho de mídia incluam um exemplo/modelo. Por favor, faça upload de um arquivo de mídia.",
      });
    } else if (!/^https?:\/\/.+/i.test(headerMediaUrl)) {
      issues.push({
        severity: SEVERITY.HIGH,
        rule: "header_media_invalid_url",
        message: "URL da mídia de amostra deve começar com http:// ou https://",
        tip: "Forneça uma URL pública acessível para a mídia de exemplo.",
      });
    }
  }

  return issues;
}

/**
 * Validate a WhatsApp message template and return detailed analysis
 * 
 * @param {Object} params
 * @param {string} params.name - Template name
 * @param {string} params.category - UTILITY, MARKETING, or AUTHENTICATION
 * @param {string} params.language - Language code (pt_BR, en_US, es)
 * @param {string} params.bodyText - Body text content
 * @param {string} params.headerText - Optional header text
 * @param {string} params.headerFormat - NONE, TEXT, IMAGE, VIDEO, DOCUMENT
 * @param {string} params.headerMediaUrl - Sample media URL for non-text headers
 * @param {string} params.footerText - Optional footer text
 * @param {Array} params.buttons - Array of button objects
 * @param {Object} params.sampleValues - Map of variable name/number to sample text
 * @returns {Object} Validation result with score, issues, and summary
 */
export function validateTemplate({
  name = "",
  category = "UTILITY",
  language = "pt_BR",
  bodyText = "",
  headerText = "",
  headerFormat = "NONE",
  headerMediaUrl = "",
  footerText = "",
  buttons = [],
  sampleValues = {},
}) {
  const allIssues = [];

  // Run all validations
  allIssues.push(...validateTemplateName(name));
  if (headerFormat === "TEXT" || headerFormat === "NONE") {
    allIssues.push(...validateHeaderText(headerText, category));
  }
  allIssues.push(...validateHeaderMedia(headerFormat, headerMediaUrl, category));
  allIssues.push(...validateBodyText(bodyText, category, sampleValues));
  allIssues.push(...validateFooterText(footerText));
  allIssues.push(...validateButtons(buttons, category));
  allIssues.push(...validateCategoryAlignment(bodyText, headerText, category));
  allIssues.push(...validateSpamIndicators(bodyText, headerText));
  allIssues.push(...validateBestPractices(bodyText, headerText, footerText, category, sampleValues));

  // Calculate approval probability
  let score = 100;
  const criticalCount = allIssues.filter(i => i.severity === SEVERITY.CRITICAL).length;
  const highCount = allIssues.filter(i => i.severity === SEVERITY.HIGH).length;
  const mediumCount = allIssues.filter(i => i.severity === SEVERITY.MEDIUM).length;
  const lowCount = allIssues.filter(i => i.severity === SEVERITY.LOW).length;

  allIssues.forEach(issue => {
    score -= SEVERITY_SCORE_PENALTY[issue.severity] || 0;
  });

  // Clamp score
  score = Math.max(0, Math.min(100, score));

  // If any critical issue, cap at 25%
  if (criticalCount > 0) {
    score = Math.min(score, 25);
  }

  // If multiple high issues, cap at 50%
  if (highCount >= 2) {
    score = Math.min(score, 50);
  }

  // Determine overall status
  let status;
  let statusLabel;
  if (score >= 85) {
    status = "excellent";
    statusLabel = "Excelente chance de aprovação";
  } else if (score >= 70) {
    status = "good";
    statusLabel = "Boa chance de aprovação";
  } else if (score >= 50) {
    status = "moderate";
    statusLabel = "Chance moderada - corrija os problemas";
  } else if (score >= 25) {
    status = "poor";
    statusLabel = "Provável rejeição - muitos problemas";
  } else {
    status = "critical";
    statusLabel = "Será rejeitado - corrija os problemas críticos";
  }

  // Character counts
  const charCounts = {
    header: { current: (headerText || "").length, max: LIMITS.HEADER_TEXT_MAX },
    body: { current: (bodyText || "").length, max: LIMITS.BODY_MAX },
    footer: { current: (footerText || "").length, max: LIMITS.FOOTER_MAX },
  };

  // Variables info
  const variables = extractVariables(bodyText || "").concat(extractVariables(headerText || ""));
  const uniqueVars = [...new Set(variables)];

  return {
    score,
    status,
    statusLabel,
    issues: allIssues.sort((a, b) => {
      const order = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
      return (order[a.severity] || 5) - (order[b.severity] || 5);
    }),
    summary: {
      critical: criticalCount,
      high: highCount,
      medium: mediumCount,
      low: lowCount,
      info: allIssues.filter(i => i.severity === SEVERITY.INFO).length,
      total: allIssues.length,
    },
    charCounts,
    variables: uniqueVars,
    hasVariables: uniqueVars.length > 0,
  };
}

// Export constants for use in components
export {
  SEVERITY,
  SEVERITY_LABELS,
  SEVERITY_COLORS,
  LIMITS,
  extractVariables,
  isPositionalVar,
  isNamedVar,
};
